import { rrulestr, RRule } from 'rrule';

import { rruleConfig } from '../constants/translation';

const capitalizeFirstLetter = ([firstChar, ...rest]) => `${firstChar.toUpperCase()}${rest.join('')}`;

const RRULE_WEEKDAY_TO_DAY_NAME_INDEX = {
  [RRule.MO.weekday]: 1,
  [RRule.TU.weekday]: 2,
  [RRule.WE.weekday]: 3,
  [RRule.TH.weekday]: 4,
  [RRule.FR.weekday]: 5,
  [RRule.SA.weekday]: 6,
  [RRule.SU.weekday]: 0,
};

const WEEKLY_INTERVAL_PHRASES = {
  1: 'Varje vecka',
  2: 'Varannan vecka',
  3: 'Var tredje vecka',
  4: 'Var fjärde vecka',
  5: 'Var femte vecka',
  6: 'Var sjätte vecka',
};

const pluralizeWeekday = (name) => (name.endsWith('dag') ? `${name}ar` : name);

const toWeekdayNumbers = (byweekday) => (byweekday || []).map((weekday) => (
  typeof weekday === 'number' ? weekday : weekday.weekday
));

const formatWeekdayList = (byweekday) => {
  const weekdayNumbers = [...new Set(toWeekdayNumbers(byweekday))].sort((a, b) => a - b);
  const names = weekdayNumbers.map((weekdayNumber) => {
    const dayNameIndex = RRULE_WEEKDAY_TO_DAY_NAME_INDEX[weekdayNumber];
    return pluralizeWeekday(rruleConfig.language.dayNames[dayNameIndex]);
  });

  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} och ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} och ${names[names.length - 1]}`;
};

const formatWeeklyIntervalPhrase = (interval) => (
  WEEKLY_INTERVAL_PHRASES[interval] || `Var ${interval}:e vecka`
);

const formatWeeklyRrule = (rule) => {
  const { interval, byweekday } = rule.options;
  const weekPhrase = formatWeeklyIntervalPhrase(interval || 1);
  const days = formatWeekdayList(byweekday);

  if (!days) return weekPhrase;
  return `${weekPhrase} på ${days}`;
};

/**
 * Human-readable Swedish summary for an RRULE string.
 * Weekly rules get natural phrasing (e.g. "Varannan vecka på torsdagar");
 * other frequencies fall back to rrule's toText().
 */
export const formatRruleSwedish = (rruleString, { capitalize = false } = {}) => {
  if (!rruleString) return '';

  try {
    const rule = rrulestr(rruleString);
    const text = rule.options.freq === RRule.WEEKLY
      ? formatWeeklyRrule(rule)
      : rule.toText(rruleConfig.getText, rruleConfig.language, rruleConfig.dateFormat);

    return capitalize ? capitalizeFirstLetter(text) : text;
  } catch {
    return '';
  }
};
